import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  Chip,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  PhoneAndroid as AndroidIcon,
  Apple as AppleIcon,
  Laptop as LaptopIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Bolt as BoltIcon,
  WifiOff as WifiOffIcon,
  IosShare as IosShareIcon,
  MoreVert as MoreVertIcon,
  AddBox as AddBoxIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import logo from '../../assets/logo.png';

interface PwaInstallModalProps {
  open: boolean;
  onClose: () => void;
  isInstallable: boolean;
  isInstalled: boolean;
  onInstall: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} id={`pwa-tabpanel-${index}`}>
      {value === index && <Box sx={{ pt: 2.5 }}>{children}</Box>}
    </div>
  );
};

export const PwaInstallModal: React.FC<PwaInstallModalProps> = ({
  open,
  onClose,
  isInstallable,
  isInstalled,
  onInstall,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState<number>(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          boxShadow: isDark
            ? '0 24px 48px -12px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 255, 255, 0.08)'
            : '0 24px 48px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          backgroundImage: 'none',
        },
      }}
    >
      {/* Top Hero Banner com Gradiente Elegante */}
      <Box
        sx={{
          position: 'relative',
          background: isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(135deg, #0284c7 0%, #1d4ed8 50%, #4f46e5 100%)',
          color: '#ffffff',
          px: 3.5,
          pt: 3.5,
          pb: 3,
          overflow: 'hidden',
        }}
      >
        {/* Elemento de iluminação decorativa ao fundo */}
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          {/* Card com o Logo da Aplicação */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 3,
                bgcolor: '#ffffff',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.8)',
              }}
            >
              <Box component="img" src={logo} alt="SysUp Logo" sx={{ maxHeight: 38, maxWidth: '100%', objectFit: 'contain' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em" sx={{ color: '#ffffff', fontSize: '1.25rem' }}>
                  SysUp App
                </Typography>
                <Chip
                  label="PWA"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 20,
                    backdropFilter: 'blur(4px)',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontWeight: 400 }}>
                Gerenciamento completo na palma da mão
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
              backdropFilter: 'blur(4px)',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Recursos / Vantagens do App */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Chip
            icon={<BoltIcon style={{ color: '#fde047', fontSize: 16 }} />}
            label="Ultra Rápido"
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              color: '#ffffff',
              fontSize: '0.725rem',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          />
          <Chip
            icon={<WifiOffIcon style={{ color: '#67e8f9', fontSize: 16 }} />}
            label="Modo Offline"
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              color: '#ffffff',
              fontSize: '0.725rem',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          />
          <Chip
            icon={<AutoAwesomeIcon style={{ color: '#a7f3d0', fontSize: 16 }} />}
            label="Sem Loja de Apps"
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              color: '#ffffff',
              fontSize: '0.725rem',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          />
        </Box>
      </Box>

      {/* Conteúdo Principal */}
      <DialogContent sx={{ px: 3.5, py: 3 }}>
        {isInstalled ? (
          <Fade in timeout={400}>
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                px: 2,
                borderRadius: 3,
                bgcolor: isDark ? alpha('#10b981', 0.08) : alpha('#10b981', 0.05),
                border: `1px solid ${alpha('#10b981', 0.2)}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: alpha('#10b981', 0.15),
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color={isDark ? '#34d399' : '#059669'}>
                Aplicativo Já Instalado!
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={360}>
                O SysUp já está pronto para uso direto da sua tela inicial ou área de trabalho.
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Botão de Ação Direta para Navegadores que suportam prompt automático */}
            {isInstallable && (
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.06),
                  border: `1.5px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  textAlign: 'center',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="primary">
                  Instalação Direta Disponível
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<GetAppIcon />}
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    onInstall();
                    onClose();
                  }}
                  sx={{
                    py: 1.4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    boxShadow: '0 8px 20px -4px rgba(37, 99, 235, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                      boxShadow: '0 12px 24px -4px rgba(37, 99, 235, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Instalar SysUp Agora
                </Button>
              </Box>
            )}

            {/* Abas com Passo a Passo por Plataforma */}
            <Box>
              <Typography variant="overline" fontWeight={700} color="text.secondary" letterSpacing="0.08em" sx={{ display: 'block', mb: 1 }}>
                Instruções por Dispositivo
              </Typography>

              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha('#000000', 0.03),
                  borderRadius: 2.5,
                  p: 0.5,
                  minHeight: 44,
                  '& .MuiTabs-indicator': {
                    bgcolor: isDark ? 'primary.main' : 'primary.main',
                    borderRadius: 2,
                    height: '100%',
                    zIndex: 0,
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  },
                  '& .MuiTab-root': {
                    minHeight: 38,
                    borderRadius: 2,
                    fontSize: '0.825rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    zIndex: 1,
                    color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      color: '#ffffff',
                    },
                  },
                }}
              >
                <Tab icon={<AndroidIcon fontSize="small" />} iconPosition="start" label="Android" />
                <Tab icon={<AppleIcon fontSize="small" />} iconPosition="start" label="iOS (iPhone)" />
                <Tab icon={<LaptopIcon fontSize="small" />} iconPosition="start" label="Computador" />
              </Tabs>

              {/* Tab 0: Android */}
              <CustomTabPanel value={activeTab} index={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <StepCard
                    step="1"
                    title="Abra o menu do Chrome"
                    description="Toque nos 3 pontos verticais no canto superior direito do navegador."
                    icon={<MoreVertIcon color="primary" fontSize="small" />}
                    isDark={isDark}
                  />
                  <StepCard
                    step="2"
                    title="Selecione Instalar aplicativo"
                    description='Toque na opção "Instalar aplicativo" ou "Adicionar à tela inicial".'
                    icon={<GetAppIcon color="primary" fontSize="small" />}
                    isDark={isDark}
                  />
                  <StepCard
                    step="3"
                    title="Confirme a instalação"
                    description='Clique em "Instalar" na janela pop-up para adicionar ao seu celular.'
                    icon={<CheckCircleIcon color="success" fontSize="small" />}
                    isDark={isDark}
                  />
                </Box>
              </CustomTabPanel>

              {/* Tab 1: iOS */}
              <CustomTabPanel value={activeTab} index={1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <StepCard
                    step="1"
                    title="Toque no botão Compartilhar"
                    description="No Safari do iPhone ou iPad, toque no ícone de compartilhamento na barra inferior."
                    icon={<IosShareIcon color="primary" fontSize="small" />}
                    isDark={isDark}
                  />
                  <StepCard
                    step="2"
                    title="Role para baixo e selecione Adicionar"
                    description='Toque na opção "Adicionar à Tela de Início".'
                    icon={<AddBoxIcon color="primary" fontSize="small" />}
                    isDark={isDark}
                  />
                  <StepCard
                    step="3"
                    title="Conclua a adição"
                    description='Toque em "Adicionar" no canto superior direito da tela.'
                    icon={<CheckCircleIcon color="success" fontSize="small" />}
                    isDark={isDark}
                  />
                </Box>
              </CustomTabPanel>

              {/* Tab 2: Computador */}
              <CustomTabPanel value={activeTab} index={2}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <StepCard
                    step="1"
                    title="Barra de Endereços"
                    description="Procure pelo ícone de instalação (computador com seta para baixo) do lado direito da URL."
                    icon={<GetAppIcon color="primary" fontSize="small" />}
                    isDark={isDark}
                  />
                  <StepCard
                    step="2"
                    title="Ou pelo Menu do Navegador"
                    description='Abra o menu (3 pontos) e escolha "Instalar SysUp...".'
                    icon={<MoreVertIcon color="primary" fontSize="small" />}
                    isDark={isDark}
                  />
                  <StepCard
                    step="3"
                    title="Pronto!"
                    description="O SysUp abrirá em uma janela própria, igual a um software nativo."
                    icon={<CheckCircleIcon color="success" fontSize="small" />}
                    isDark={isDark}
                  />
                </Box>
              </CustomTabPanel>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Botão Fechar no Rodapé */}
      <Box sx={{ px: 3.5, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          fullWidth
          sx={{
            py: 1,
            borderRadius: 2.5,
            fontWeight: 700,
            textTransform: 'none',
            color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
            borderColor: isDark ? alpha('#ffffff', 0.15) : alpha('#000000', 0.15),
            '&:hover': {
              borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              bgcolor: isDark ? alpha('#ffffff', 0.05) : alpha('#000000', 0.03),
            },
          }}
        >
          Entendido, Fechar
        </Button>
      </Box>
    </Dialog>
  );
};

// Componente auxiliar para os cards de passos
interface StepCardProps {
  step: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  isDark: boolean;
}

const StepCard: React.FC<StepCardProps> = ({ step, title, description, icon, isDark }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      p: 1.75,
      borderRadius: 2.5,
      bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#000000', 0.02),
      border: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#000000', 0.05)}`,
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: isDark ? alpha('#ffffff', 0.05) : alpha('#000000', 0.035),
        transform: 'translateX(2px)',
      },
    }}
  >
    <Box
      sx={{
        minWidth: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: '0.75rem',
        boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)',
        mt: 0.25,
      }}
    >
      {step}
    </Box>

    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography variant="subtitle2" fontWeight={700} fontSize="0.9rem">
          {title}
        </Typography>
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary" fontSize="0.8rem" lineHeight={1.4}>
        {description}
      </Typography>
    </Box>
  </Box>
);

export default PwaInstallModal;
